/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertBanner,
  Box,
  Button,
  ButtonIcon,
  Chip,
  FooterActionBar,
  Icon,
  InputField,
  Menu,
  MenuItem,
  Typography,
} from '@saviynt/design-system';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import usePostIDProvider from '../../../../hooks/use-postidprovider';
import useUpdateIDPProvider from '../../../../hooks/use-updateidprovider';
import {
  type IDPConfigKosmos,
  type RootState,
} from '../../../../services/types';
import { isEmpty, useInvertedLocalize } from '../../../../utils';
import { type Provider } from '../../constants';

import invertedMessages from './messages';

import './KosmosSettings.css';

interface IDPSetupPageProps {
  isOpen: boolean;
  onClose: () => void;
  provider: Provider | undefined;
  currentProvider: IDPConfigKosmos | undefined;
  refetchIDPProviders: () => void;
  setIsEnableSuccessVisible: (val: boolean) => void;
  setAlertTitle: (val: string) => void;
  setAlertStatus: (val: string) => void;
  setIsOpenEnableModal: (val: boolean) => void;
  setShowSuccessBanner: (val: boolean) => void;
  currentVendor: Provider | undefined;
}

function KosmosSettings({
  isOpen,
  onClose,
  provider,
  currentProvider,
  refetchIDPProviders,
  setIsEnableSuccessVisible,
  setAlertTitle,
  setAlertStatus,
  setIsOpenEnableModal,
  setShowSuccessBanner,
  currentVendor,
}: IDPSetupPageProps) {
  const [license, setLicense] = useState(currentProvider?.config?.LICENSE);
  const [tenantDNS, setTenantDNS] = useState(
    currentProvider?.config?.TENANTDNS
  );
  const [dvcID, setDvcID] = useState(currentProvider?.config?.DVCID);
  const [showCriticalBanner, setShowCriticalBanner] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLicenseVisible, setIsLicenseVisible] = useState(false);
  const [dNS, setDNS] = useState(currentProvider?.config?.DNS);
  const [community, setCommunity] = useState(
    currentProvider?.config?.COMMUNITYNAME
  );
  const [isErrorAlertVisible, setIsErrorAlertVisible] = useState(false);
  const { isMenuPinned } = useSelector((state: RootState) => state.user);

  const l = useInvertedLocalize(invertedMessages);

  useEffect(() => {
    if (currentProvider) {
      setLicense(currentProvider.config?.LICENSE);
      setTenantDNS(currentProvider.config?.TENANTDNS);
      setDvcID(currentProvider.config?.DVCID);
      setTenantDNS(currentProvider.config?.DNS);
      setCommunity(currentProvider.config?.COMMUNITYNAME);
    }
  }, [currentProvider]);

  const { mutate: saveIDPConfigKosmos } = usePostIDProvider();
  const { mutate: updateIDPConfigKosmos, isPending: isUpdateInProgress } =
    useUpdateIDPProvider();

  const handleSave = () => {
    if (provider) {
      const vendorConfig = {
        vendorName: provider.id,
        status: 0,
        config: {
          LICENSE: license,
          DVCID: dvcID,
          TENANTDNS: tenantDNS,
          DNS: dNS,
          COMMUNITYNAME: community,
        },
      };

      if (!isEmpty(currentProvider)) {
        vendorConfig.status = currentProvider?.status ?? 0;
      }

      if (!isEmpty(currentProvider) && license?.trim() === '') {
        setIsErrorAlertVisible(true);

        return;
      }

      setIsErrorAlertVisible(false);

      updateIDPConfigKosmos(vendorConfig as IDPConfigKosmos, {
        onSuccess(response) {
          if (response.errorMessage) {
            setErrorMessage(response.errorMessage);
            setShowCriticalBanner(true);
          } else {
            onClose();
            refetchIDPProviders();
            setShowSuccessBanner(true);
          }
        },
      });
    } else if (currentVendor) {
      const vendorConfig = {
        vendorName: currentVendor.id,
        status: 0,
        config: {
          LICENSE: license,
          DVCID: dvcID,
          TENANTDNS: tenantDNS,
          DNS: dNS,
          COMMUNITYNAME: community,
        },
      };

      saveIDPConfigKosmos(vendorConfig as IDPConfigKosmos, {
        onSuccess(response) {
          if (response.errorMessage) {
            setErrorMessage(response.errorMessage);
            setShowCriticalBanner(true);
          } else {
            onClose();
            refetchIDPProviders();
          }
        },
      });
    }
  };

  const licenseField = useMemo(
    () => (
      <InputField
        name='license'
        type={isLicenseVisible ? 'text' : 'password'}
        suffixButtonType='password'
        suffixIsToggle={isLicenseVisible}
        suffixOnClick={() => setIsLicenseVisible((prev) => !prev)}
        label={
          <Typography kind='label' htmlFor='License'>
            {l('License')}
          </Typography>
        }
        placeholder={l('Enter License')}
        value={license}
        setValue={setLicense}
        isCritical={isErrorAlertVisible}
        setIsCritical={setIsErrorAlertVisible}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setLicense(e.target.value)
        }
        className='Ext-KosmosSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
      />
    ),
    [
      license,
      setLicense,
      l,
      isLicenseVisible,
      isErrorAlertVisible,
      setIsErrorAlertVisible,
    ]
  );

  const dvcIDField = useMemo(
    () => (
      <InputField
        name='dvcID'
        label={
          <Typography kind='label' htmlFor='dvcID'>
            {l('dvcID')}
          </Typography>
        }
        placeholder={l('Enter dvcID')}
        value={dvcID}
        setValue={setDvcID}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setDvcID(e.target.value)
        }
        className='Ext-KosmosSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
      />
    ),
    [dvcID, setDvcID, l]
  );

  const tenantDNSField = useMemo(
    () => (
      <InputField
        name='tenantDNS'
        label={
          <Typography kind='label' htmlFor='TenantDNS'>
            {l('TenantDNS')}
          </Typography>
        }
        placeholder={l('Enter TenantDNS')}
        value={tenantDNS}
        setValue={setTenantDNS}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setTenantDNS(e.target.value)
        }
        className='Ext-KosmosSettings-content-inputField'
        isRequired
        maxLength={100}
        minRequiredChars={3}
        shouldDisallowBrackets={false}
      />
    ),
    [tenantDNS, setTenantDNS, l]
  );

  const dNSField = useMemo(
    () => (
      <InputField
        name='dNS'
        label={
          <Typography kind='label' htmlFor='DNS'>
            {l('DNS')}
          </Typography>
        }
        placeholder={l('Enter DNS')}
        value={dNS}
        setValue={setDNS}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setDNS(e.target.value)
        }
        className='Ext-KosmosSettings-content-inputField'
        maxLength={255}
        isRequired
        minRequiredChars={3}
      />
    ),
    [dNS, setDNS, l]
  );
  const communityField = useMemo(
    () => (
      <InputField
        name='community'
        label={
          <Typography kind='label' htmlFor='Community Name'>
            {l('Community Name')}
          </Typography>
        }
        placeholder={l('Enter Community Name')}
        value={community}
        setValue={setCommunity}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setCommunity(e.target.value)
        }
        className='Ext-KosmosSettings-content-inputField'
        maxLength={255}
        isRequired
        minRequiredChars={3}
      />
    ),
    [community, setCommunity, l]
  );

  const isSaveDisabled =
    isEmpty(license) ||
    isEmpty(tenantDNS) ||
    isEmpty(dvcID) ||
    isEmpty(dNS) ||
    isEmpty(community) ||
    isUpdateInProgress;

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className='Ext-KosmosSettings'
      role='dialog'
      aria-labelledby='setUpPageTitle'
      aria-hidden={!isOpen}>
      <div className='Ext-KosmosSettings-contentWrapper'>
        {currentProvider && provider && isEmpty(currentVendor) && (
          <div className='Ext-KosmosSettings-header'>
            <Box className='Ext-KosmosSettings-header-text'>
              <ButtonIcon
                size='large'
                kind='ghost'
                icon={<Icon kind='ChevronLeft' />}
                onClick={onClose}
              />
              <Icon
                className='Ext-KosmosSettings-icon'
                kind={provider.icon}
                size='xLarge'
              />
              <Box className='Ext-KosmosSettings-header-flexbox'>
                <Typography kind='h2' color='--color-foreground-neutral-bold'>
                  {provider.name}
                </Typography>
                {currentProvider && (
                  <Chip
                    size='xSmall'
                    label={
                      currentProvider.status === 1 ? 'Enabled' : 'Disabled'
                    }
                    color={currentProvider.status === 1 && 'success'}
                  />
                )}
              </Box>
            </Box>
            {currentProvider && provider && (
              <Menu
                className='Ext-KosmosSettings-header-menu'
                options={[
                  {
                    label: currentProvider.status === 0 ? 'Disable' : 'Enable',
                    value: currentProvider.status === 0 ? 'Disable' : 'Enable',
                  },
                ]}
                CustomMenu={
                  <div>
                    <MenuItem
                      option={{
                        label:
                          currentProvider.status === 0 ? 'Enable' : 'Disable',
                        value:
                          currentProvider.status === 0 ? 'Enable' : 'Disable',
                      }}
                      onClick={() => {
                        if (currentProvider.status === 0) {
                          const providerConfig = currentProvider;

                          providerConfig.status = 1;
                          updateIDPConfigKosmos(providerConfig, {
                            onSuccess(response) {
                              if (response.errorMessage) {
                                setErrorMessage(response.errorMessage);
                                setShowCriticalBanner(true);
                                setIsOpenEnableModal(false);
                              } else {
                                setIsEnableSuccessVisible(true);
                                setAlertTitle(provider.name);
                                setAlertStatus('Enabled');
                                setIsOpenEnableModal(false);
                                onClose();
                                refetchIDPProviders();
                              }
                            },
                          });
                        } else {
                          setIsOpenEnableModal(false);
                          const providerConfig = currentProvider;

                          providerConfig.status = 0;
                          updateIDPConfigKosmos(providerConfig, {
                            onSuccess(response) {
                              if (response.errorMessage) {
                                setErrorMessage(response.errorMessage);
                                setShowCriticalBanner(true);
                              } else {
                                setIsEnableSuccessVisible(true);
                                setAlertTitle(provider.name);
                                setAlertStatus('Disabled');
                                onClose();
                                refetchIDPProviders();
                              }
                            },
                          });
                        }
                      }}
                      tabIndex={isOpen ? 0 : -1}
                    />
                  </div>
                }
                trigger={<Icon kind='MoreVertical' size='medium' />}
              />
            )}
          </div>
        )}
        {currentVendor && (
          <div className='Ext-KosmosSettings-breadcrumb'>
            <div className='Ext-KosmosSettings-breadcrumb-header'>
              <Typography
                kind='body2-bold'
                color='--color-foreground-primary-medium'>
                {l('ID proofing')}
              </Typography>
              <Icon kind='ChevronRight' size='xSmall' />
              <Typography
                kind='body2'
                color='--color-foreground-neutral-medium'>
                {currentVendor.name}
              </Typography>
            </div>
            <div className='Ext-KosmosSettings-breadcrumb-provider'>
              <Icon
                className='Ext-KosmosSettings-icon'
                kind={currentVendor.icon}
                size='xLarge'
              />
              <Typography kind='h1'>{currentVendor.name}</Typography>
            </div>
          </div>
        )}
        <AlertBanner
          colorTheme='Warning'
          kind='low'
          title={l('Invalid input')}
          description={l('Please enter valid input.')}
          isVisible={!isSaveDisabled && isErrorAlertVisible}
          onCancel={() => setIsErrorAlertVisible(false)}
          shouldAutoDismiss
          isFloating
          position={{
            type: 'absolute',
            top: '8rem',
            left: '7rem',
          }}
        />
        <AlertBanner
          colorTheme='Critical'
          title={errorMessage}
          description=''
          isVisible={showCriticalBanner}
          onCancel={() => setShowCriticalBanner(false)}
          isFloating
          position={{
            type: 'absolute',
            top: '8rem',
            left: '7rem',
          }}
          shouldAutoDismiss
          isColonVisible={false}
        />
        <div className='Ext-KosmosSettings-content'>
          {licenseField}
          {tenantDNSField}
          {dvcIDField}
          {dNSField}
          {communityField}
        </div>
      </div>
      <div
        className={classNames(
          'Ext-KosmosSettings-footer',
          isMenuPinned && 'Ext-KosmosSettings-footer--isMenuPinned'
        )}>
        <FooterActionBar>
          <Button kind='outlined' onClick={onClose}>
            {l('Back')}
          </Button>
          <Button
            kind='filled'
            onClick={handleSave}
            isLoading={isUpdateInProgress}
            loaderText='Saving'
            isDisabled={isSaveDisabled}>
            {l('Save')}
          </Button>
        </FooterActionBar>
      </div>
    </div>
  );
}

export default KosmosSettings;
